import { useMemo } from 'react'
import { View, Text, Platform } from 'react-native'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS, type SoundId } from '../../../src/shared/constants/sounds.constants'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import {
  getPillButtonGroupStyles,
  type PillButtonGroupVariant,
} from './PillButtonGroup.styles'

export type { PillButtonGroupVariant }

export interface PillButtonOption<T extends string = string> {
  key: T
  label: string
  disabled?: boolean
}

export interface PillButtonGroupProps<T extends string = string> {
  options: ReadonlyArray<PillButtonOption<T>>
  value: T
  onChange: (key: T) => void
  variant?: PillButtonGroupVariant
  soundId?: SoundId
  testID?: string
}

export function PillButtonGroup<T extends string>({
  options,
  value,
  onChange,
  variant = 'social',
  soundId,
  testID,
}: PillButtonGroupProps<T>) {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const styles = useMemo(
    () => getPillButtonGroupStyles(theme, variant, themeMode),
    [theme, variant, themeMode],
  )

  return (
    <View style={styles.container} testID={testID}>
      {options.map((opt) => {
        const isActive = opt.key === value
        const isDisabled = opt.disabled ?? false
        return (
          <SoundPressable
            key={opt.key}
            tapSoundId={soundId ?? SOUND_IDS.UI_TAP}
            playTapSound={soundId != null}
            accessibilityRole="button"
            disabled={isDisabled}
            onPress={() => !isDisabled && onChange(opt.key)}
            style={({ pressed, hovered }) => [
              styles.pill,
              isActive && styles.pillActive,
              isDisabled && styles.pillDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              !isDisabled && pressed && styles.pillPressed,
              !isDisabled && (hovered ?? false) && Platform.OS === 'web' && styles.pillHover,
            ]}
            testID={testID ? `${testID}-${opt.key}` : undefined}
          >
            <Text
              style={[
                styles.pillText,
                isActive && styles.pillTextActive,
                isDisabled && styles.pillTextDisabled,
              ]}
            >
              {opt.label}
            </Text>
          </SoundPressable>
        )
      })}
    </View>
  )
}
